import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CANVAS_DIM_MAX,
  CANVAS_DIM_MIN,
  ScreenResolution,
  clampCanvasDimension,
  isCustomResolutionPreset,
  isSameNewProjectResolutionCard,
  newProjectResolutionPresets,
  resolutionFromCustomSize,
} from '@models/settings.model';
import { PersistenceService, ProjectEntry } from '@app/services/persistence.service';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';

export interface StartupResult {
  projectName: string;
  resolution: ScreenResolution;
  action: 'new' | 'load';
  loadProjectId?: string;
}

@Component({
  selector: 'app-startup-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, PxlNumberStepperDirective],
  templateUrl: './startup-dialog.html',
  styleUrls: ['./startup-dialog.css']
})
export class StartupDialog implements OnInit {
  @Output() confirmed  = new EventEmitter<StartupResult>();
  @Output() importRequested = new EventEmitter<void>();

  view: 'manager' | 'new' = 'manager';
  projectName   = 'Untitled Project';
  selectedRes: ScreenResolution = newProjectResolutionPresets[1];
  resPresets    = newProjectResolutionPresets;
  customW = 64;
  customH = 64;
  readonly CANVAS_DIM_MIN = CANVAS_DIM_MIN;
  readonly CANVAS_DIM_MAX = CANVAS_DIM_MAX;
  savedProjects: ProjectEntry[] = [];
  loading       = true;
  renamingId: string | null = null;
  renameValue   = '';
  deleteConfirmId: string | null = null;
  loadError = '';

  constructor(private persistence: PersistenceService, private cdr: ChangeDetectorRef) {}

  async ngOnInit(): Promise<void> {
    try {
      this.savedProjects = await this.withTimeout(this.persistence.listProjects(), 5000, []);
      this.loadError = '';
    } catch {
      // Never leave the startup dialog stuck on "Loading your projects..."
      this.savedProjects = [];
      this.loadError = 'Could not load local projects right now.';
    } finally {
      this.loading = false;
      this.view = 'manager';
      this.cdr.markForCheck();
    }
  }

  selectRes(r: ScreenResolution): void {
    this.selectedRes = r;
    if (isCustomResolutionPreset(r)) {
      this.customW = clampCanvasDimension(this.customW);
      this.customH = clampCanvasDimension(this.customH);
    }
  }

  isSameResCard = isSameNewProjectResolutionCard;
  isCustomPreset = isCustomResolutionPreset;

  previewDims(r: ScreenResolution): { w: number; h: number } {
    if (r.label === 'Custom') {
      return { w: clampCanvasDimension(this.customW), h: clampCanvasDimension(this.customH) };
    }
    return { w: r.width, h: r.height };
  }

  previewAspectRatio(r: ScreenResolution): string {
    const d = this.previewDims(r);
    return `${d.w}/${d.h}`;
  }

  isPreviewTallerThanWide(r: ScreenResolution): boolean {
    const d = this.previewDims(r);
    return d.h > d.w;
  }

  displayDimsText(r: ScreenResolution): string {
    if (r.label === 'Custom' && isCustomResolutionPreset(this.selectedRes)) {
      return `${clampCanvasDimension(this.customW)}×${clampCanvasDimension(this.customH)}`;
    }
    return `${r.width}×${r.height}`;
  }

  // ── New Project ────────────────────────────────────────────────
  openNew(): void {
    this.projectName = 'Untitled Project';
    this.selectedRes = newProjectResolutionPresets[1];
    this.customW = 64;
    this.customH = 64;
    this.view = 'new';
  }

  async create(): Promise<void> {
    if (!this.projectName.trim()) this.projectName = 'Untitled Project';
    const resolution = isCustomResolutionPreset(this.selectedRes)
      ? resolutionFromCustomSize(this.customW, this.customH)
      : this.selectedRes;
    const { width: w, height: h } = resolution;
    if (w < CANVAS_DIM_MIN || h < CANVAS_DIM_MIN || w > CANVAS_DIM_MAX || h > CANVAS_DIM_MAX) {
      window.alert(`Canvas size must be between ${CANVAS_DIM_MIN} and ${CANVAS_DIM_MAX} pixels.`);
      return;
    }
    this.confirmed.emit({ projectName: this.projectName, resolution, action: 'new' });
  }

  // ── Load ───────────────────────────────────────────────────────
  openProject(p: ProjectEntry): void {
    if (this.renamingId === p.id) return; // don't open while renaming
    this.confirmed.emit({ projectName: p.name, resolution: this.selectedRes, action: 'load', loadProjectId: p.id });
  }

  // ── Rename ─────────────────────────────────────────────────────
  startRename(p: ProjectEntry, e: Event): void {
    e.stopPropagation();
    this.renamingId = p.id;
    this.renameValue = p.name;
    setTimeout(() => (document.getElementById('rename-input') as HTMLInputElement)?.select(), 10);
  }

  async commitRename(p: ProjectEntry): Promise<void> {
    if (!this.renameValue.trim()) this.renameValue = p.name;
    // Load project data, re-save with new name
    const data = await this.persistence.loadProject(p.id);
    if (data) await this.persistence.saveProject(p.id, this.renameValue.trim(), data.frames, data.currentFrameId);
    this.renamingId = null;
    this.savedProjects = await this.withTimeout(this.persistence.listProjects(), 5000, []);
    this.cdr.markForCheck();
  }

  cancelRename(): void { this.renamingId = null; }

  // ── Delete ─────────────────────────────────────────────────────
  confirmDelete(p: ProjectEntry, e: Event): void {
    e.stopPropagation();
    this.deleteConfirmId = p.id;
  }

  async executeDelete(p: ProjectEntry, e: Event): Promise<void> {
    e.stopPropagation();
    await this.persistence.deleteProject(p.id);
    this.deleteConfirmId = null;
    this.savedProjects = await this.withTimeout(this.persistence.listProjects(), 5000, []);
    this.cdr.markForCheck();
  }

  cancelDelete(e: Event): void { e.stopPropagation(); this.deleteConfirmId = null; }

  // ── Import ─────────────────────────────────────────────────────
  importFile(): void { this.importRequested.emit(); }

  // ── Utils ──────────────────────────────────────────────────────
  formatDate(ts: number): string {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
    return new Promise(resolve => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve(fallback);
        }
      }, ms);
      promise.then(
        value => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        },
        () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(fallback);
        }
      );
    });
  }
}
